// Enhanced store with real-time event handling
import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import { modalitiesDatas } from "../config/constants";
import {
  handleTranscription,
  pausedRecordHandler,
  resumedRecordHandler,
  startingRecordHandler,
  stoppedRecordHandler,
} from "./handler/handler";
import api, { API_BASEURL } from "../config/axiosInstance";
import axios from "axios";
// import useAugnitoStore from "./useAugnitoStore";

const useTranscriptionStore = create(
  subscribeWithSelector((set, get) => ({
    // App State
    isInitialized: false,
    isRecording: false,
    isPaused: false,
    showSessionPrompt: false,
    isProcessingSpeech: false,
    medicalSuggestions: {},
    audioLevel: 0,

    userRole: '',
    userId: '',

    studyName: "",

    // Audio Configuration
    modality: 'mri',
    audioSource: "uploadAzure",
    selectedMicDevice: null,
    audioDevices: [],
    isLoadingDevices: false,
    deviceError: null,
    countUnread: 0,

    // Sessions
    currentSession: null,
    sessions: [],
    totalSessions: 0,
    isLoadingSessions: false,

    // Transcript
    techniques: [],
    transcript: [],
    aiTranscript: ``,
    searchQuery: "",

    // Providers
    providers: [],
    selectedProvider: null,

    // Real-time Status
    vadEnabled: true,
    recordingStatus: "ready",

    auth: null,

    // Audio Playback State
    playbackState: "stopped",
    playbackPosition: 0,
    playbackDuration: 0,
    playbackSpeed: 1.0,
    loadedAudioSessionId: null,

    // Report Section State
    currentReport: "",
    mriReport: null,
    isReportVisible: true,
    reportSaved: true,

    // Split View State
    transcriptReportSplit: [60, 40],

    // Errors
    errors: [],

    instantClicks: [],
    bodyParts: [],
    examine: [],
    macro: [],
    studies: [],
    allStudies: [],
    clients: [],
    radiologists: [],
    modalities: [],
    scribes: [],
    study: null,

    selectedClient: null,
    selectedRadiologist: null,
    selectedScribe: null,

    highlightedLine: null,
    setHighlightedLine: (line) => set({ highlightedLine: line }),
    clearHighlightedLine: () => set({ highlightedLine: null }),

    transcriptionText: ``,
    // Settings (matching backend)
    settings: {
      language: "en-US",
      audioDevice: null,
      theme: "dark",
      autoScroll: true,
      saveLocation: "data/exports",
      audioSource: "microphone",
      sampleRate: 16000,
      noiseReduction: true,
      autoGainControl: true,
      vadEnabled: true,
      fontSize: 14,
      showConfidence: false,
      compactMode: false,
      defaultExportFormat: "txt",
      exportLocation: "./exports",
      exportTimestamps: true,
      exportConfidence: false,
      exportSpeakers: true,
      autoSaveInterval: 30,
      hardwareAcceleration: true,
      analytics: false,
      crashReports: true,
    },

    // Enhanced API calls with error handling
    apiCall: async (method, ...args) => {
      try {
        if (!window.pywebview?.api?.[method]) {
          return
        }
        const result = await window.pywebview.api[method](...args);
        if (result && result.success === false) {
          return
        }
        return result;
      } catch (error) {
        console.error(`API call ${method} failed:`, error);
        get().addError(`api_${method}`, error.message);
        return
      }
    },
    sessionShowPrompt: async (isCheck = false) => {
      if (isCheck) {
        const { currentSession, isRecording, isPaused, isProcessingSpeech } =
          get();
        const hasSessionData =
          currentSession &&
          (currentSession.audio_files?.length > 0 ||
            currentSession.has_audio === true ||
            currentSession.word_count > 0);

        if (hasSessionData) {
          set({ showSessionPrompt: true });
          return {
            continue: false,
          };
        } else {
          set({ showSessionPrompt: true });
          return {
            continue: true,
          };
        }
      } else if (isCheck === false) {
        set({ showSessionPrompt: false });
        return;
      }
    },
    setCountUnread: async (c) => {
      set({
        countUnread: c
      });
    },


    setStudyName: async (name) => {
      set({
        studyName: name
      });
    },


    clearSession: async () => {
      set({
        isRecording: false,
        isPaused: false,
        isProcessingSpeech: false,
        recordingStatus: "ready",
        transcript: [],
        currentReport: "",
        currentSession: null,
      });
    },

    setAuth: async (data) => {
      localStorage.setItem("access_token", data.access_token);
      localStorage.setItem("name", data.name);
      localStorage.setItem("userId", data.userId);
      localStorage.setItem("userRole", data.userRole);

      api.defaults.headers.common["Authorization"] =
        "Bearer " + data.access_token;

      axios.defaults.headers.common["Authorization"] =
        "Bearer " + data.access_token;

      set({ auth: data });
    },
    unAuthorizeUser: async () => {
      localStorage.clear();

      api.defaults.headers.common["Authorization"] = null;

      axios.defaults.headers.common["Authorization"] = null;

      set({ auth: null });



    },

    // Enhanced initialization
    initializeApp: async () => {
      try {
        if (
          !localStorage.getItem("access_token") &&
          !localStorage.getItem("name") &&
          !localStorage.getItem("userId") &&
          !localStorage.getItem("userRole")
        ) {
          await get().unAuthorizeUser();

          set({ isInitialized: true });
          return;
        } else {
          await get().setAuth({
            access_token: localStorage.getItem("access_token"),
            userId: localStorage.getItem("userId"),
            name: localStorage.getItem("name"),
            userRole: localStorage.getItem("userRole"),

          });
        }
        const storedProvider = localStorage.getItem("provider");
        const storedSource = localStorage.getItem("source");
        const userRole = localStorage.getItem("userRole");
        const userId = localStorage.getItem("userId");

        const defaultAudioSource = userRole === "RADIOLOGIST" ? "microphone" : "uploadAzure";
        const defaultProvider = userRole === "RADIOLOGIST" ? "augnito" : "azure";

        const providers = await get().getProviders();

        const settings = {};

        const audioDevices = [];

        await get().getSessions();
        await get().getTechs();
        await get().getInstanceClick();
        await get().getExamine();
        await get().getMacros();
        await get().getBodyParts();
        await get().getStudies();
        await get().getAllClients();
        await get().getAllRadiologists();
        await get().getAllScribes();
        await get().getAllModalities();

        set({
          providers,
          audioDevices,
          audioSource: defaultAudioSource,
          selectedProvider: defaultProvider,
          settings: { ...get().settings, ...settings },
          isInitialized: true,
          userRole: userRole,
          userId: userId,
        });

        // Setup real-time event listeners
        get().setupRealTimeEvents();
      } catch (error) {
        console.error(" Failed to initialize app:", error);
        get().addError("initialization_error", error.message);
        set({ isInitialized: true });
      }
    },

    setSelectedClient: async (client) => {
      set({
        selectedClient: client,
      });
    },
    setSelectedRadiologist: async (rad) => {
      set({
        selectedRadiologist: rad,
      });
    },

    setSelectedScribe: async (scribe) => {
      set({
        selectedScribe: scribe,
      });
    },

    getInstanceClick: async (search, bodyPartId, examinationId) => {
      const instantData = await api.get(
        `/api/v1/instantclicks?examinationId=${examinationId ? examinationId : ""
        }&bodyPartId=${bodyPartId ? bodyPartId : ""}&search=${search ? search : ""
        }&limit=10`
      );
      set({
        instantClicks:
          instantData?.data?.data?.length > 0 ? instantData?.data?.data : [],
      });
    },
    getProviders: async () => {
      const providerData = await api.get(
        `/api/v1/providers?page=1&limit=5&sortBy=created_at&order=desc`
      );

      return providerData?.data?.data?.length > 0
        ? providerData?.data?.data
        : [];
    },
    getMacros: async (search, bodyPartId, examinationId) => {
      const macrosData = await api.get(
        `/api/v1/macros?examinationId=${examinationId ? examinationId : ""
        }&bodyPartId=${bodyPartId ? bodyPartId : ""}&search=${search ? search : ""
        }&limit=10`
      );
      set({
        macro: macrosData?.data?.data?.length > 0 ? macrosData?.data?.data : [],
      });
    },
    getExamine: async (search, bodyPartId) => {
      const examineData = await api.get(
        `/api/v1/examine?bodyPartId=${bodyPartId ? bodyPartId : ""}&search=${search ? search : ""
        }&limit=10`
      );
      set({
        examine:
          examineData?.data?.data?.length > 0 ? examineData?.data?.data : [],
      });
    },
    getTechs: async (search, bodyPartId) => {
      const techniquesData = await api.get(
        `/api/v1/technique?bodypartId=${bodyPartId ? bodyPartId : ""}&search=${search ? search : ""
        }&limit=100`
      );
      set({
        techniques:
          techniquesData?.data?.techniques?.length > 0 ? techniquesData?.data?.techniques : [],
      });
    },
    getStudies: async (search) => {
      try {
        const studies = await api.get('/api/v1/datasets')
        set({
          studies: studies.data?.data?.map((i) => {
            return {
              label: i.studyName,
              value: i?._id,
            };
          }),
        });
      } catch (error) {

      }
    },
    getAllClients: async (search) => {
      try {
        const clients = await api.get(`/api/v1/user?role=CLIENT&search=${search ? search : ""}&limit=200&page=1`)
        set({
          clients: clients.data?.data?.map((i) => {
            return {
              label: i?.firstName + (i?.lastName ? ' ' + i?.lastName : ''),
              value: i?._id,
            };
          }),
        });
      } catch (error) {

      }
    },
    getAllRadiologists: async (search) => {
      try {
        const radiologists = await api.get(`/api/v1/user?role=RADIOLOGIST&search=${search ? search : ""}&limit=200&page=1`)
        set({
          radiologists: radiologists.data?.data?.map((i) => {
            return {
              label: i?.firstName + (i?.lastName ? ' ' + i?.lastName : ''),
              value: i?._id,
            };
          }),
        });
      } catch (error) {

      }
    },
    getAllScribes: async (search) => {
      try {
        const scribes = await api.get(`/api/v1/user?role=SCRIBE&search=${search ? search : ""}&limit=200&page=1`)
        set({
          scribes: scribes.data?.data?.map((i) => {
            return {
              label: i?.firstName + (i?.lastName ? ' ' + i?.lastName : ''),
              value: i?._id,
            };
          }),
        });
      } catch (error) {

      }
    },
    getAllModalities: async (search) => {
      try {
        const modalities = await api.get(`/api/v1/modalities?search=${search ? search : ""}&limit=200&page=1`)
        set({
          modalities: modalities.data?.data?.map((i) => {
            return {
              label: i?.name,
              value: i?._id,
            };
          }),
        });
      } catch (error) {

      }
    },
    setStudies: async (st, modality, bodyPartId) => {

      let allDatas = []
      try {

        let resp = null
        if (st) {
          resp = await api.get(`/api/v1/datasets/${st}?modality=${modality ? modality : ''}&bodyPartId=${bodyPartId ? bodyPartId : ''}`)

          allDatas = resp.data?.data?.data?.length > 0 ? resp.data?.data?.data : []

        } else {
          resp = await api.get(`/api/v1/datasets?modality=${modality ? modality : ''}&bodyPartId=${bodyPartId ? bodyPartId : ''}`)
          if (resp.data.data?.length > 0) {
            st = resp.data?.data?.[0]?._id
            get().setStudyName({
              label: resp.data?.data?.[0]?.studyName,
              value: resp.data?.data?.[0]?._id,
            })
            allDatas = resp.data?.data?.length > 0 ? resp.data?.data?.[0]?.data : []
          }
        }
      } catch (error) {

      }
      set({
        allStudies: allDatas,
        study: st,
      });
    },
    setModality: async (modality) => {
      set({
        modality: modality,
      });
    },
    setAiTranscript: async (aiT) => {
      set({
        aiTranscript: aiT,
      });
    },
    getResourceUsage: async () => {
      const result = await get().apiCall("get_resource_usage");
      return result;
    },
    getBodyParts: async (search) => {
      const bodyPartsData = await api.get(
        `/api/v1/bodyparts?search=${search ? search : ""}&limit=1000`
      );
      set({
        bodyParts:
          bodyPartsData?.data?.data?.length > 0
            ? bodyPartsData?.data?.data
            : [],
      });
    },

    setTranscriptionText: async (text) => {
      set({
        transcriptionText: text,
      });
    },

    getSessions: async (
      page = 1,
      limit = 10,
      query = "",
      sortBy = "last_updated",
      order = "desc",
      client = "",
      radiologist = "",
      status = '0',
      qc_status = null,
      from_date = '',
      to_date = ''
    ) => {
      try {

        const params = {};

        if (query) params.search = query;
        if (page) params.page = page;
        if (limit) params.limit = limit;
        if (sortBy) params.sortBy = sortBy;
        if (order) params.order = order;
        if (status !== null && status !== undefined) params.status = status === "" ? '' : status;
        if (qc_status !== null && qc_status !== undefined) params.qc_status = qc_status == "" ? null : qc_status;
        if (radiologist) params.radiologist = radiologist;
        if (client) params.client = client;
        if (from_date) params.dateFrom = from_date;
        if (to_date) params.dateTo = to_date;


        const response = await api.get(`/api/v1/sessions`, { params });

        const newSessions = response?.data?.data || [];
        const total = response?.data?.pagination?.total ?? 0;
        set((state) => ({
          sessions: newSessions,
          totalSessions: total,
        }));
      } catch (error) {
        console.error("Failed to fetch sessions:", error);
        set({
          sessions: [],
          totalSessions: 0,
        });
      }
    },

    closeCurrentSession: async () => {
      const { currentSession, isRecording, isPaused, isProcessingSpeech } =
        get();
      if (!currentSession) return;

      if (isRecording || isPaused || isProcessingSpeech) {
        get().addError(
          "session_close_error",
          "Please Stop Current Transcription First!"
        );

        return;
      }

      set({
        isRecording: false,
        isPaused: false,
        isProcessingSpeech: false,
        recordingStatus: "ready",
        transcript: [],
        currentReport: "",
        aiTranscript: '',
        currentSession: null,
      });
      get().getSessions();
    },

    saveReport: async (savedReport, status) => {
      const { currentSession, currentReport } = get();
      if (!currentSession) return false;

      try {
        const updatedSession = {
          ...currentSession,
          report: savedReport,
          last_updated: new Date().toISOString(),
        };


        await api.patch("/api/v1/sessions/" + currentSession._id, {
          report: savedReport,
          report_status: status
        });

        get().setCurrentSession(currentSession._id);
        set((state) => ({
          currentReport: savedReport,
          reportSaved: true,
        }));
        return true;
      } catch (error) {
        console.error("Failed to save report:", error);
        get().addError("report_error", error.message);
        return false;
      }
    },

    updateTranscriptSegment: async (segmentId, updates) => {
      const { transcript } = get();

      await api.patch(`/api/v1/sessions/update-segments/${segmentId}`, updates);

      const updated = transcript.map((segment) =>
        segment._id === updates._id ? { ...segment, ...updates } : segment
      );


      set((state) => ({
        transcript: updated,
      }));
    },

    // Enhanced real-time event handling
    setupRealTimeEvents: () => {
      window.backendCallback = (method, data) => {
        const { currentSession } = get();
        switch (method) {
          case "recording_started":
            startingRecordHandler(set, currentSession);
            break;

          case "recording_paused":
            pausedRecordHandler(set, currentSession);
            break;

          case "recording_resumed":
            resumedRecordHandler(set, currentSession);
            break;

          case "recording_stopped":
            stoppedRecordHandler(set, get, currentSession, data);
            break;

          case "transcription_update":
            // console.log(data)
            handleTranscription(set, get, data);
            break;

          case "audio_file_saved":
            // console.log(data);
            break;

          case "status_update":
            if (data?.isProcessing) {
              set({
                isProcessingSpeech: true,
              });
            }
            break;

          default:
            console.warn("Unknown backend callback:", method);
        }
      };
    },

    getAudioDevices: async () => {
      // set({ isLoadingDevices: true, deviceError: null });
      // try {
      //   const result = await get().apiCall("get_audio_devices");
      //   set({
      //     audioDevices: result.devices || [],
      //     isLoadingDevices: false,
      //     deviceError: null,
      //   });
      //   return result.devices || [];
      // } catch (error) {
      //   console.error("Failed to get audio devices:", error);
      //   set({ deviceError: error.message, isLoadingDevices: false });
      //   get().addError("device_error", error.message);
      // return
      // }
    },

    startRecording: async (isAugnitoWidget = false) => {
      try {
        const {
          currentSession,
          audioSource,
          providers,
          selectedProvider: initialProvider,
          allStudies,
        } = get();

        // Determine provider and source dynamically
        const provider = initialProvider;
        const source = audioSource;

        if (!provider) {
          const error = "No provider selected";
          get().addError("recording_error", error);
          return
        }

        // Now safely resolve configSchema
        const configSchema = providers?.find((p) => p.name === provider)?.config_schema;

        let sessionToUse = currentSession;
        if (!sessionToUse) {
          const error = "Please choose case or create new case";
          get().addError("recording_error", error);
          return
          // sessionToUse = await get().createNewSession();
        }

        // if (provider === "augnito") {
        //   const augnito = useAugnitoStore.getState();
        //   augnito.init(provider, source, sessionToUse._id, configSchema);
        //   set({ currentSession: sessionToUse });
        //   return;
        // }
        const result = await window.pywebview.api.start_transcription(
          provider,
          source,
          sessionToUse._id,
          configSchema,
          allStudies
        );

        if (!result.success) {
          const error = result.error || "Failed to start recording";
          get().addError("recording_error", error);
          return
        }

        set({ currentSession: sessionToUse });

      } catch (error) {
        console.error(" Failed to start recording:", error);
        get().addError("recording_error", error.message);
        return
      }
    },

    pauseRecording: async () => {
      try {
        const { currentSession, selectedProvider } = get();

        // if (selectedProvider === "augnito") {
        //   useAugnitoStore.getState().pauseRecording();
        //   return;
        // }

        const result = await window.pywebview.api.pause_transcription(
          currentSession._id
        );

        if (!result.success) {
          const error = result.error || "Failed to pause recording";
          get().addError("recording_error", error);
          return
        }
      } catch (error) {
        console.error(" Failed to pause recording:", error);
        get().addError("recording_error", error.message);
        return
      }
    },

    stopRecording: async () => {
      try {
        const { currentSession, selectedProvider } = get();
        // if (selectedProvider === "augnito") {
        //   useAugnitoStore.getState().stopRecording();
        //   return;
        // }

        const result = await window.pywebview.api.stop_transcription(
          currentSession._id
        );
        if (!result.success) {
          const error = result.error || "Failed to stop recording";
          get().addError("recording_error", error);
          return
        }
      } catch (error) {
        console.error(" Failed to stop recording:", error);
        get().addError("recording_error", error.message);
        return
      }
    },

    setMedicalSuggestions: async (suggestion) => {
      try {
        set({
          medicalSuggestions: suggestion
        })
      } catch (error) {
        console.error(" Failed ", error);
        get().addError("sugestion_error", error.message);
        return
      }
    },

    resumeRecording: async () => {
      try {
        const { currentSession, selectedProvider } = get();
        // if (selectedProvider === "augnito") {
        //   useAugnitoStore.getState().resumeRecording();
        //   return;
        // }
        const result = await window.pywebview.api.resume_transcription(
          currentSession._id
        );

        if (!result.success) {
          const error = result.error || "Failed to resume recording";
          get().addError("recording_error", error);
          return
        }
      } catch (error) {
        console.error(" Failed to resume recording:", error);
        get().addError("recording_error", error.message);
        return
      }
    },

    updateDataByField: async (field, value) => {
      try {
        const { currentSession, sessions } = get();

        if (!currentSession) return;

        // const sessionIndex = sessions.findIndex(
        //   (s) => s._id === currentSession._id
        // );

        // if (sessionIndex === -1) return;

        // const updatedSession = {
        //   ...sessions[sessionIndex],
        //   [field]: value,
        // };

        // const updatedSessions = [...sessions];
        // updatedSessions[sessionIndex] = updatedSession;

        // set(() => ({
        //   currentSession: {
        //     ...currentSession,
        //     [field]: value,
        //   },
        // }));

        await api.patch(`/api/v1/sessions/${currentSession._id}`, {
          [field]: value,
        });
        await get().getSessions();
        await get().setCurrentSession(currentSession._id);
      } catch (error) {
        console.error(`Failed to update ${field}:`, error);
        get().addError("session_update_error", error.message);
      }
    },
    updateMultipleFields: async (fieldsObject) => {
      try {
        const { currentSession, sessions } = get();
        if (!currentSession) return;

        // const sessionIndex = sessions.findIndex(s => s._id === currentSession._id);
        // if (sessionIndex === -1) return;

        // const updatedSession = {
        //   ...sessions[sessionIndex],
        //   ...fieldsObject,
        // };

        // const updatedSessions = [...sessions];
        // updatedSessions[sessionIndex] = updatedSession;

        // set(() => ({
        //   currentSession: {
        //     ...currentSession,
        //     ...fieldsObject,
        //   },
        // }));

        await api.patch(`/api/v1/sessions/${currentSession._id}`, fieldsObject);
        await get().getSessions();
        await get().setCurrentSession(currentSession._id);
      } catch (error) {
        console.error("Failed to update session fields:", error);
        get().addError("session_update_error", error.message);
      }
    },
    updateNotes: async (field, value, toid) => {
      try {
        const { currentSession } = get();

        if (!currentSession) return;
        await api.patch(`/api/v1/sessions/${currentSession._id}?to=${toid}`, {
          [field]: value,
        });
        await get().getSessions();
        await get().setCurrentSession(currentSession._id);
      } catch (error) {
        console.error(`Failed to update ${field}:`, error);
        get().addError("session_update_error", error.message);
      }
    },
    updateCaseName: async (name) => {
      try {
        const { currentSession, sessions } = get();

        const sessionIndex = sessions.findIndex(
          (s) => s._id === currentSession._id
        );

        if (sessionIndex === -1) return;

        const updatedSession = {
          ...sessions[sessionIndex],
          name,
        };

        const updatedSessions = [...sessions];
        updatedSessions[sessionIndex] = updatedSession;

        set(() => ({
          sessions: updatedSessions,
          currentSession: {
            ...currentSession,
            name,
          },
        }));
      } catch (error) {
        console.error("Failed to update case name:", error);
      }
    },
    updateAccessionNumber: async (accessionText) => {
      try {
        const { currentSession, sessions } = get();

        const sessionIndex = sessions.findIndex(
          (s) => s._id === currentSession._id
        );

        if (sessionIndex === -1) return;

        const updatedSession = {
          ...sessions[sessionIndex],
          accession_No: accessionText,
        };

        const updatedSessions = [...sessions];
        updatedSessions[sessionIndex] = updatedSession;

        set(() => ({
          sessions: updatedSessions,
          currentSession: {
            ...currentSession,
            accession_No: accessionText,
          },
        }));
      } catch (error) {
        console.error("Failed to update case name:", error);
      }
    },

    refreshSessions: async () => {
      try {
        window.location.reload();
        // call apii sessions all
      } catch (error) {
        console.error("Failed to refresh sessions:", error);
        set({ isLoadingSessions: false });
        get().addError("session_error", error.message);
      }
    },

    getRecordingStatus: () => {
      const { isRecording, isPaused, isProcessingSpeech, recordingStatus } =
        get();
      if (!isRecording) return "ready";
      if (isPaused) return "paused";
      if (isProcessingSpeech) return "processing";
      return recordingStatus || "listening";
    },

    // Enhanced event handler

    dismissError: (errorId) => {
      set((state) => ({
        errors: state.errors.map((error) =>
          error.id === errorId ? { ...error, dismissed: true } : error
        ),
      }));
    },

    setAudioSource: (source) => {
      set({ audioSource: source });
      // localStorage.setItem('source', source)
      get().updateSettings({ audioSource: source });
    },

    updateSettings: async (newSettings) => {
      try {
        const currentSettings = get().settings;
        const updatedSettings = { ...currentSettings, ...newSettings };

        // const result = await get().apiCall("update_settings", updatedSettings);

        // if (result.success) {
        //   set({ settings: updatedSettings });

        //   // Update related state
        //   if (newSettings.audioSource) {
        //     set({ audioSource: newSettings.audioSource });
        //   }
        //   if (newSettings.audioDevice !== undefined) {
        //     set({ selectedMicDevice: newSettings.audioDevice });
        //   }

        //   return true;
        // } else {
        //   get().addError(
        //     "settings_error",
        //     result.error || "Failed to update settings"
        //   );
        //   return false;
        // }
      } catch (error) {
        console.error("Failed to update settings:", error);
        get().addError("settings_error", error.message);
        return false;
      }
    },

    setSearchQuery: (query) => set({ searchQuery: query }),

    copyTranscript: () => {
      const { transcript } = get();
      const text = transcript
        .filter((segment) => segment.is_final)
        .map((segment) => segment.text)
        .join("\n");

      navigator.clipboard.writeText(text).catch((error) => {
        console.error("Failed to copy transcript:", error);
        get().addError("copy_error", "Failed to copy transcript to clipboard");
      });
    },

    clearTranscript: () => set({ transcript: [] }),

    // createNewSession: async (isNew) => {
    //   const { selectedProvider, totalSessions, sessions } = get();
    //   if (!selectedProvider) {
    //     const error = "No provider selected";
    //     get().addError("session_error", error);
    //     return
    //   }

    //   const caseName = `Enter Case Name`;

    //   try {
    //     const resp = await api.post("/api/v1/sessions/", {
    //       name: caseName,
    //     });

    //     if (isNew) {
    //       set((state) => ({
    //         currentSession: resp?.data?.data,
    //         transcript: [],
    //         currentReport: "",
    //         sessions: [...state.sessions, resp?.data?.data],
    //         totalSessions: state.totalSessions + 1,
    //       }));
    //     }

    //     return resp?.data?.data;
    //   } catch (error) {
    //     console.error("Failed to create session:", error);
    //     get().addError("session_error", error.message);
    //     return
    //   }
    // },

    createNewSession: async (payload, isNew = true) => {
      const { selectedProvider, totalSessions, sessions } = get();

      if (!selectedProvider) {
        const error = "No provider selected";
        get().addError("session_error", error);
        return
      }

      try {
        const resp = await api.post("/api/v1/sessions/", { ...payload, from: 'Application' });
        if (isNew) {
          get().setCurrentSession(resp?.data?.data?._id)
        }

        return resp?.data?.data;
      } catch (error) {
        console.error("Failed to create session:", error);
        get().addError("session_error", error.message);
        return
      }
    },

    bulkUploadSessions: async (file) => {
      if (!file) {
        console.error("Missing file");
        return;
      }

      try {
        const formData = new FormData();
        formData.append("file", file);

        await api.post("/api/v1/excel-template", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        await get().getSessions();

      } catch (error) {
        console.error("Bulk upload failed:", error);
        get().addError("session_error", error.message);
        return;
      }
    },

    uploadAudioFileAzure: async (file) => {
      const { currentSession, study } = get();
      if (!file || !currentSession?._id) {
        console.error("Missing file or session ID");
        return;
      }

      try {
        // Calculate duration before upload
        const url = URL.createObjectURL(file);
        const duration = await new Promise((resolve, reject) => {
          const audio = new Audio();
          audio.preload = "metadata";
          audio.src = url;
          audio.onloadedmetadata = () => resolve(audio.duration || 0);
          audio.onerror = reject;
        });
        URL.revokeObjectURL(url);

        // Send file, session id, and duration
        const formData = new FormData();
        formData.append("file", file);
        formData.append("study", study);
        formData.append("sessionid", currentSession._id);
        formData.append("duration", duration);

        const res = await api.post("/api/v1/transcription/azure", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        get().setCurrentSession(currentSession._id);

      } catch (error) {
        console.error("Failed to upload audio file:", error);
      }
    },

    handleVerifyReport: async (reportText) => {
      const { currentSession } = get();
      try {
        const resp = await api.post("/api/v1/datasets/verify-qc-report", {
          sessionId: currentSession ? currentSession?._id : null,
          reportText: reportText
        });
        get().setCurrentSession(currentSession._id);
        return resp?.data?.data;
      } catch (error) {
        console.error("Failed to update report:", error);
        get().addError("session_error", error.message);
        return
      }
    },

    uploadAudioFileEleven: async (file) => {
      const { currentSession } = get();
      if (!file || !currentSession?._id) {
        console.error("Missing file or session ID");
        return;
      }

      try {
        const url = URL.createObjectURL(file);
        const duration = await new Promise((resolve, reject) => {
          const audio = new Audio();
          audio.preload = "metadata";
          audio.src = url;
          audio.onloadedmetadata = () => resolve(audio.duration || 0);
          audio.onerror = reject;
        });
        URL.revokeObjectURL(url);

        // Send file, session id, and duration
        const formData = new FormData();
        formData.append("file", file);
        // formData.append("study", study);
        formData.append("sessionid", currentSession._id);
        formData.append("duration", duration);

        await api.post("/api/v1/transcription/elevenlabs", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        get().setCurrentSession(currentSession._id);

      } catch (error) {
        console.error("Failed to upload audio file:", error);
      }
    },

    setSelectedProvider: (provider) => {
      // localStorage.setItem('provider', provider)
      set({ selectedProvider: provider });
    },

    addError: (type, message) => {
      const error = {
        id: Date.now().toString(),
        type,
        message,
        timestamp: Date.now(),
        dismissed: false,
      };

      set((state) => ({
        errors: [...state.errors, error],
      }));

      console.error(`Error [${type}]: ${message}`);
    },

    setCurrentSession: async (sessionId) => {

      const { isRecording, isPaused } = get();

      const sessionData = await api.get(`/api/v1/sessions/${sessionId}`);
      if (sessionData?.data?.data) {
        const session = sessionData?.data?.data;

        let extractedSuggestions = {};
        if (Array.isArray(session.segments)) {
          session.segments.forEach((seg) => {
            if (seg.suggestions && Object.keys(seg.suggestions).length > 0) {
              Object.entries(seg.suggestions).forEach(([keyword, suggestionGroups]) => {
                suggestionGroups.forEach((sg) => {
                  if (sg.suggestions && sg.suggestions.length > 0) {
                    extractedSuggestions[keyword] = [
                      ...(extractedSuggestions[keyword] || []),
                      ...sg.suggestions,
                    ];
                  }
                });
              });
            }
          });
        }

        if (!isRecording || !isPaused) {
          set({
            transcript: session?.segments || [],
            currentReport: session.report || "",
          })
        }



        get().setStudies('', session?.modality?.name, session?.bodypart)
        // setStudyName(stud)


        set({
          currentSession: session,
          selectedClient: session?.client?._id ?? null,
          selectedRadiologist: session?.radiologist?._id ?? null,
          selectedScribe: session?.scribe?._id ?? null,
          reportSaved: true,
          medicalSuggestions: {
            ...get().medicalSuggestions,
            ...extractedSuggestions,
          },
        });
      }
    },

    clearErrors: () => set({ errors: [] }),

    getTranscriptStats: () => {
      const { transcript } = get();
      const finalSegments = transcript.filter((segment) => segment.is_final);

      return {
        totalSegments: transcript.length,
        finalSegments: finalSegments.length,
        wordCount: finalSegments.reduce((count, segment) => {
          return count + (segment.text?.split(" ").length || 0);
        }, 0),
        duration:
          transcript.length > 0
            ? Math.max(
              ...transcript.map((s) => s.session_timestamp || s.timestamp)
            ) -
            Math.min(
              ...transcript.map((s) => s.session_timestamp || s.timestamp)
            )
            : 0,
      };
    },

    generateReport: async (
      selectedBodyPartId,
      selectedExaminationId,
      selectedMacroId,
      selectedInstantClickId,
      reportText
    ) => {
      const {
        transcript,
        isRecording,
        isPaused,
        currentReport,
        isProcessingSpeech,
        providers,
        currentSession
      } = get();

      if (isRecording || isPaused || isProcessingSpeech) {
        get().addError("report_error", "Please Stop Current Session First!");
      }
      if (!transcript.length) {
        get().addError("report_error", "No transcription to generate report.");
        return;
      }

      const api_key = providers?.find((i) => i.name === "whisper")
        ?.config_schema?.api_key;

      if (!api_key) {
        get().addError("report_error", "Please Configure Providers Properly.");
        return;
      }

      const finalTranscription = transcript
        .filter((segment) => segment.is_final)
        .map((segment) => segment.text)
        .join("\n");

      if (!finalTranscription) {
        get().addError("report_error", "No transcription to generate report.");
      }

      const contents = [
        "You are an expert radiology report generator.",
        "Always respond only in **raw HTML** format.",
        "All reports must follow this structure: use <h2>Findings</h2> and <h2>Impression</h2>.",
        "Use bullet points <ul><li>...</li></ul> where applicable for clarity.",
        "If a report template is provided, use it as the exact base and replace only relevant sections based on the transcription.",
        "When replacing, preserve the structure, phrasing, and section order of the template. Do not paraphrase or rewrite existing negatives.",
        "Only fix minor transcription errors (e.g., typos or misspelled words). Do not change sentence structure unless required for correction.",
        "If no template is provided, generate a new professional radiology report **based only on the given transcription**.",
        "Never invent new findings or measurements that are not clearly present in the transcription.",
        "If absolutely no anatomical terms, vertebral levels, or imaging-related findings are present, return the following warning message only: '⚠️ The transcription is not relevant to a radiology report.'"
      ];

      if (selectedBodyPartId || selectedExaminationId || selectedMacroId || selectedInstantClickId) {
        contents.push(
          `Here is the base template report. Update it properly using the transcription. Template : ${reportText || currentSession.report || currentReport}`,
        );
      }




      // try {
      //   const payload = {
      //     model: "gpt-4o-mini", // or "gpt-4", "gpt-4-turbo"
      //     temperature: 0,
      //     top_p: 1,
      //     presence_penalty: 0,
      //     max_tokens: 6048,
      //     messages: [
      //       {
      //         role: "system",
      //         content: contents.join(" "),
      //       },
      //       {
      //         role: "user",
      //         content: `Generate the HTML report from this transcription paragraph:\n\n${finalTranscription}`,
      //       },
      //     ],
      //   };

      //   const data = JSON.stringify(payload);

      //   const response = await fetch(
      //     "https://api.openai.com/v1/chat/completions",
      //     {
      //       method: "POST",
      //       headers: {
      //         "Content-Type": "application/json",
      //         Authorization: `Bearer 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`,
      //       },
      //       body: data,
      //     }
      //   );

      //   const { choices } = await response.json();

      //   const aiReport = choices[0].message.content
      //     ?.replace("```html", "")
      //     ?.replace("```", "");

      //   if (!aiReport) {
      //     get().addError("report_error", "Failed to Generate Report");
      //   }

      //   set({
      //     currentReport: aiReport,
      //   });

      //   return { status: true, report: aiReport };
      // } catch (error) {
      //   console.error("Failed to generate report:", error);
      //   get().addError("report_error", "Failed to Generate Report");
      // }



      try {
        const resp = await api.post("/api/v1/datasets/generate-claude-report", {
          selectedBodyPartId,
          selectedExaminationId,
          selectedMacroId,
          selectedInstantClickId,
          finalTranscription,
          currentSession: currentSession?._id,
          reportText: reportText || currentSession.report || currentReport
        });


        const aiReport = resp?.data?.data
          ?.replace("```html", "")
          ?.replace("```", "");

        if (!aiReport) {
          get().addError("report_error", "Failed to Generate Report");
        }

        set({
          currentReport: aiReport,
        });

        return { status: true, report: aiReport };
      } catch (error) {

      }

    },

    convertAudioFileWhisper: async (file) => {
      try {
        const { currentSession, providers } = get();

        const api_key = providers?.find((i) => i.name === "whisper")
          ?.config_schema?.api_key;

        if (!api_key) {
          get().addError(
            "report_error",
            "Please Configure Providers Properly."
          );
          return;
        }

        const form = new FormData();
        form.append("file", file);
        // form.append("model", "gpt-4o-mini-transcribe");
        // form.append("model", "gpt-4o-mini-transcribe");
        form.append("model", "gpt-4o-transcribe");
        form.append("language", "en");
        form.append("response_format", "json");
        form.append("temperature", "0");

        const res = await fetch(
          "https://api.openai.com/v1/audio/transcriptions",
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${api_key}`,
            },
            body: form,
          }
        );

        if (!res.ok) {
          get().addError(
            "conversation_error",
            "Failed to Generate Transcription"
          );

          return false;
        }
        const data = await res.json();

        if (!data.text) {
          get().addError("conversion_error", "Failed to Convert Audio File");
        }

        let sessionToUse = currentSession;
        if (!sessionToUse) {
          sessionToUse = await get().createNewSession();
        }

        const existingSegments = sessionToUse?.segments || [];

        const enhancedSegment = {
          ...data,
          wordCount: data.text ? data.text.split(" ").length : 0,
          isNewSentence: data.text ? /^[A-Z]/.test(data.text.trim()) : false,
          endsWithPunctuation: data.text
            ? /[.!?]$/.test(data.text.trim())
            : false,
          id: `${Math.random().toString(36).substr(2, 9)}`,
          timestamp: Date.now() / 1000,
          session_timestamp: Date.now() / 1000,
          is_final: true,
          provider: "whisper",
          text: data.text || "",
          confidence: 0,
          duration: 0,
          processing_time: new Date(),
        };

        const finalSegments = existingSegments.filter((seg) => seg.is_final);
        const updatedSegments = [...finalSegments, enhancedSegment];

        const finalSession = {
          ...sessionToUse,
          segments: updatedSegments,
        };

        const url = URL.createObjectURL(file);

        const duration = await new Promise((resolve, reject) => {
          const audio = new Audio();
          audio.preload = "metadata";
          audio.src = url;
          audio.onloadedmetadata = () => resolve(audio.duration); // seconds (float)
          audio.onerror = reject;
        });

        const formData = new FormData();
        formData.append("file", file);
        formData.append("duration", duration);
        formData.append("audio_duration", duration);
        formData.append("size", file.size || 0);
        formData.append(
          "format",
          file.type || file.name.split(".").pop() || "unknown"
        );
        formData.append("session_id", sessionToUse._id);
        formData.append("provider", "whisper");
        formData.append("language", "en-US");
        formData.append(
          "segments",
          updatedSegments?.length > 0 ? JSON.stringify(updatedSegments) : "[]"
        );
        formData.append(
          "word_count",
          updatedSegments.reduce((count, segment) => {
            return count + (segment.text?.split(" ").length || 0);
          }, 0)
        );

        await api.patch(`/api/v1/sessions/${sessionToUse._id}`, formData);

        set({
          transcript: updatedSegments,
          currentSession: finalSession,
        });

        await get().getSessions();

        return true;
      } catch (error) {
        get().addError("conversion_error", "Failed to Convert Audio File");
      }
    },
  }))
);

export { useTranscriptionStore };
